import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as subscriptionApi from "../services/subscriptionApi";
import { extractSubscriptionApiError } from "../utils/normalizeAdminPlanPayload";

function formatDateField(dateVal) {
  if (!dateVal) return null;
  if (Array.isArray(dateVal) && dateVal.length === 3) {
    const [year, month, day] = dateVal;
    const monthStr = String(month).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    return `${year}-${monthStr}-${dayStr}`;
  }
  if (typeof dateVal === "string") return dateVal;
  return null;
}

/**
 * Backend dates to a strict "yyyy-MM-dd" string (or "" when absent/invalid) — the only format
 * <input type="date"> accepts. Handles Java LocalDate arrays [2026,8,13], ISO and ISO-datetime
 * strings, epoch numbers and Date objects, so the panel renders whichever shape an endpoint sends.
 */
function toIsoDate(value) {
  if (value === null || value === undefined || value === "") return "";

  if (Array.isArray(value)) {
    const [year, month, day] = value;
    if (!year || !month || !day) return "";
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
    return isoMatch ? isoMatch[1] : "";
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function normalizeSubscriptionRow(data) {
  if (!data || typeof data !== "object") return null;
  return {
    id: data.id ?? data.subscriptionId ?? data.storeSubscriptionId,
    planId: data.planId ?? data.subscriptionPlanId,
    status: data.status,
    planSnapshot: data.planSnapshot,
    commissionPercentage: data.commissionPercentage,
    commissionPercentageTax: data.commissionPercentageTax,
    perOrderAmount: data.perOrderAmount,
    perOrderAmountPercentage: data.perOrderAmountPercentage,
    billingMonths: data.billingMonths ?? data.planSnapshot?.billingMonths,
    startDate: toIsoDate(data.startDate),
    endDate: toIsoDate(data.endDate),
    currentStartDate: toIsoDate(data.currentStartDate),
    currentEndDate: toIsoDate(data.currentEndDate),
    nextBillingDate: toIsoDate(data.nextBillingDate),
    paidCount: data.paidCount,
    remainingCount: data.remainingCount,
    totalCount: data.totalCount,
    cancelAtCycleEnd: data.cancelAtCycleEnd,
    razorpaySubscriptionId: data.razorpaySubscriptionId,
    ...(data.currency && { currency: data.currency }),
    ...(data.amount !== undefined && { amount: data.amount }),
    ...(data.durationDays !== undefined && { durationDays: data.durationDays }),
    ...(data.storeOverrides && { storeOverrides: data.storeOverrides }),
    ...(data.subscriptionPlan && { subscriptionPlan: data.subscriptionPlan }),
    ...(data.autoRenew !== undefined && { autoRenew: data.autoRenew }),
  };
}

function hasValue(v) {
  return v !== null && v !== undefined;
}

function rawLooksLikeSubscriptionRow(raw) {
  if (!raw || typeof raw !== "object") return false;
  return (
    hasValue(raw.id) ||
    hasValue(raw.subscriptionId) ||
    hasValue(raw.storeSubscriptionId) ||
    hasValue(raw.planId) ||
    hasValue(raw.subscriptionPlanId) ||
    hasValue(raw.subscriptionPlan)
  );
}

function normalizedSubscriptionIsPresent(row) {
  if (!row || typeof row !== "object") return false;
  return (
    hasValue(row.id) ||
    hasValue(row.planId) ||
    hasValue(row.subscriptionPlan) ||
    hasValue(row.planSnapshot) ||
    hasValue(row.status)
  );
}

/**
 * The Areakart entitlement view that /current returns alongside the subscription: plan-included
 * services, permanently purchased add-ons, and admin grants. Kept as sent — it was previously
 * dropped here, which is why the store could never see which services its subscription actually
 * carried.
 */
function normalizeAreakart(raw) {
  const areakart = raw?.areakart;
  if (!areakart || typeof areakart !== "object") return null;

  return {
    includedServices: Array.isArray(areakart.includedServices) ? areakart.includedServices : [],
    includedAddOns: Array.isArray(areakart.includedAddOns) ? areakart.includedAddOns : [],
    purchasedAddOns: Array.isArray(areakart.purchasedAddOns) ? areakart.purchasedAddOns : [],
    complimentary: {
      services: Array.isArray(areakart.complimentary?.services)
        ? areakart.complimentary.services
        : [],
      addOns: Array.isArray(areakart.complimentary?.addOns) ? areakart.complimentary.addOns : [],
    },
  };
}

function normalizeCurrentPayload(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      storeId: null,
      activeSubscription: null,
      upcomingSubscriptions: [],
      history: [],
      areakart: null,
    };
  }
  if (raw.activeSubscription !== undefined) {
    const normalizedActive = raw.activeSubscription
      ? normalizeSubscriptionRow(raw.activeSubscription)
      : null;
    return {
      storeId: raw.storeId ?? null,
      activeSubscription: normalizedSubscriptionIsPresent(normalizedActive) ? normalizedActive : null,
      upcomingSubscriptions: Array.isArray(raw.upcomingSubscriptions) ? raw.upcomingSubscriptions : [],
      history: Array.isArray(raw.history) ? raw.history : [],
      areakart: normalizeAreakart(raw),
    };
  }
  // Success envelope with only `storeId` (no active plan) — not a legacy flat subscription row.
  if (!rawLooksLikeSubscriptionRow(raw)) {
    return {
      storeId: raw.storeId ?? null,
      activeSubscription: null,
      upcomingSubscriptions: Array.isArray(raw.upcomingSubscriptions) ? raw.upcomingSubscriptions : [],
      history: Array.isArray(raw.history) ? raw.history : [],
      areakart: normalizeAreakart(raw),
    };
  }
  const active = normalizeSubscriptionRow(raw);
  return {
    storeId: raw.storeId ?? null,
    activeSubscription: normalizedSubscriptionIsPresent(active) ? active : null,
    upcomingSubscriptions: [],
    history: [],
    areakart: normalizeAreakart(raw),
  };
}

// --- Admin: Plans ---
export const fetchPlans = createAsyncThunk(
  "subscriptionPlan/fetchPlans",
  async (_, { rejectWithValue }) => {
    try {
      const data = await subscriptionApi.fetchPlans();
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.data)) return data.data;
      return [];
    } catch (err) {
      return rejectWithValue(extractSubscriptionApiError(err));
    }
  }
);

export const fetchPlanById = createAsyncThunk(
  "subscriptionPlan/fetchPlanById",
  async (planId, { rejectWithValue }) => {
    try {
      return await subscriptionApi.fetchPlanById(planId);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const createPlan = createAsyncThunk(
  "subscriptionPlan/createPlan",
  async (body, { rejectWithValue }) => {
    try {
      return await subscriptionApi.createPlan(body);
    } catch (err) {
      return rejectWithValue(extractSubscriptionApiError(err));
    }
  }
);

export const updatePlan = createAsyncThunk(
  "subscriptionPlan/updatePlan",
  async ({ planId, payload }, { rejectWithValue }) => {
    try {
      return await subscriptionApi.updatePlan({ planId, payload });
    } catch (err) {
      return rejectWithValue(extractSubscriptionApiError(err));
    }
  }
);

export const deletePlan = createAsyncThunk(
  "subscriptionPlan/deletePlan",
  async (planId, { rejectWithValue }) => {
    try {
      await subscriptionApi.deletePlan(planId);
      return { planId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updatePlanStatus = createAsyncThunk(
  "subscriptionPlan/updatePlanStatus",
  async ({ planId, active }, { rejectWithValue }) => {
    try {
      return await subscriptionApi.updatePlanStatus({ planId, active });
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// --- Store subscription (current + Razorpay checkout) ---
export const fetchStoreSubscriptions = createAsyncThunk(
  "subscriptionPlan/fetchStoreSubscriptions",
  async (storeId, { rejectWithValue }) => {
    try {
      return await subscriptionApi.fetchStoreSubscriptions(storeId);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        return { activeSubscription: null, upcomingSubscriptions: [], history: [] };
      }
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchStoreSubscriptionPlans = createAsyncThunk(
  "subscriptionPlan/fetchStoreSubscriptionPlans",
  async (storeId, { rejectWithValue }) => {
    try {
      const data = await subscriptionApi.fetchStoreSubscriptionPlans(storeId);
      if (Array.isArray(data)) return data;
      // Typical envelope: { plans: [...], storeId }
      if (data && Array.isArray(data.plans)) return data.plans;
      if (data && Array.isArray(data.data?.plans)) return data.data.plans;
      if (data && Array.isArray(data.data)) return data.data;
      return [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const createStoreSubscription = createAsyncThunk(
  "subscriptionPlan/createStoreSubscription",
  async ({ storeId, planId, billingMonths, addOnIds }, { rejectWithValue }) => {
    try {
      return await subscriptionApi.createStoreSubscription({
        storeId,
        planId,
        billingMonths,
        addOnIds,
      });
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const changeStoreSubscription = createAsyncThunk(
  "subscriptionPlan/changeStoreSubscription",
  async ({ storeId, planId, billingMonths, addOnIds }, { rejectWithValue }) => {
    try {
      return await subscriptionApi.changeStoreSubscription({
        storeId,
        planId,
        billingMonths,
        addOnIds,
      });
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const verifyStoreSubscription = createAsyncThunk(
  "subscriptionPlan/verifyStoreSubscription",
  async ({ storeId, razorpaySubscriptionId }, { rejectWithValue }) => {
    try {
      return await subscriptionApi.verifyStoreSubscription({ storeId, razorpaySubscriptionId });
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const cancelStoreSubscription = createAsyncThunk(
  "subscriptionPlan/cancelStoreSubscription",
  async ({ storeId, subscriptionId, cancelAtCycleEnd = true }, { rejectWithValue }) => {
    try {
      return await subscriptionApi.cancelStoreSubscription({
        storeId,
        subscriptionId,
        cancelAtCycleEnd,
      });
    } catch (err) {
      return rejectWithValue(extractSubscriptionApiError(err));
    }
  }
);

export const assignStoreSubscription = createAsyncThunk(
  "subscriptionPlan/assignStoreSubscription",
  async (body, { rejectWithValue }) => {
    try {
      return await subscriptionApi.assignStoreSubscription(body);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateStoreSubscriptionOverrides = createAsyncThunk(
  "subscriptionPlan/updateStoreSubscriptionOverrides",
  async ({ storeId, body }, { rejectWithValue }) => {
    try {
      return await subscriptionApi.updateStoreSubscriptionOverrides(storeId, body);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Legacy aliases for store-detail tab
export const getSubscriptionPlan = fetchPlans;
export const getStoreSubscriptionPlan = createAsyncThunk(
  "subscriptionPlan/getStoreSubscriptionPlan",
  async (storeId, { rejectWithValue }) => {
    try {
      return await subscriptionApi.fetchStoreSubscriptionLegacy(storeId);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const initialState = {
  plans: [],
  planDetail: null,
  storeSubscriptionStatus: null,
  storeSubscriptionPlans: [],
  loading: false,
  storePlansLoading: false,
  storeCurrentLoading: false,
  storeCheckoutLoading: false,
  storeCancelLoading: false,
  error: null,
};

export const subscriptionSlice = createSlice({
  name: "subscriptionPlan",
  initialState,
  reducers: {
    clearPlanDetail: (state) => {
      state.planDetail = null;
    },
    clearStoreSubscriptionStatus: (state) => {
      state.storeSubscriptionStatus = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state, flag) => {
      state.loading = flag;
      if (flag) state.error = null;
    };
    const setError = (state, action) => {
      state.error = action.payload;
      state.loading = false;
    };

    builder
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.plans = action.payload || [];
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPlans.pending, (state) => setLoading(state, true))
      .addCase(fetchPlans.rejected, (state, action) => setError(state, action))

      .addCase(fetchPlanById.fulfilled, (state, action) => {
        state.planDetail = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPlanById.pending, (state) => setLoading(state, true))
      .addCase(fetchPlanById.rejected, (state, action) => setError(state, action))

      .addCase(createPlan.fulfilled, (state, action) => {
        if (action.payload && action.payload.id) {
          const idx = state.plans.findIndex((p) => p.id === action.payload.id);
          if (idx >= 0) {
            state.plans[idx] = action.payload;
          } else {
            state.plans = [action.payload, ...state.plans];
          }
          if (state.planDetail && state.planDetail.id === action.payload.id) {
            state.planDetail = action.payload;
          }
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(createPlan.pending, (state) => setLoading(state, true))
      .addCase(createPlan.rejected, (state, action) => setError(state, action))

      .addCase(updatePlan.fulfilled, (state, action) => {
        if (action.payload && action.payload.id) {
          const idx = state.plans.findIndex((p) => p.id === action.payload.id);
          if (idx >= 0) {
            state.plans[idx] = action.payload;
          } else {
            state.plans = [action.payload, ...state.plans];
          }
          if (state.planDetail && state.planDetail.id === action.payload.id) {
            state.planDetail = action.payload;
          }
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updatePlan.pending, (state) => setLoading(state, true))
      .addCase(updatePlan.rejected, (state, action) => setError(state, action))

      .addCase(updatePlanStatus.fulfilled, (state, action) => {
        const payload = action.payload;
        const planId =
          (payload && typeof payload === "object" && payload.id) || action.meta.arg?.planId;
        if (planId) {
          const patch =
            payload && typeof payload === "object" && Object.keys(payload).length
              ? payload
              : { active: action.meta.arg.active };
          const idx = state.plans.findIndex((p) => p.id === planId);
          if (idx >= 0) {
            state.plans[idx] = { ...state.plans[idx], ...patch };
          }
          if (state.planDetail?.id === planId) {
            state.planDetail = { ...state.planDetail, ...patch };
          }
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updatePlanStatus.pending, (state) => setLoading(state, true))
      .addCase(updatePlanStatus.rejected, (state, action) => setError(state, action))

      .addCase(deletePlan.fulfilled, (state, action) => {
        if (action.payload?.planId) {
          state.plans = state.plans.filter((p) => p.id !== action.payload.planId);
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deletePlan.pending, (state) => setLoading(state, true))
      .addCase(deletePlan.rejected, (state, action) => setError(state, action))

      .addCase(fetchStoreSubscriptions.fulfilled, (state, action) => {
        const payload =
          action.payload?.data !== undefined ? action.payload.data : action.payload;
        state.storeSubscriptionStatus = normalizeCurrentPayload(payload ?? {});
        state.storeCurrentLoading = false;
        state.error = null;
      })
      .addCase(fetchStoreSubscriptions.pending, (state) => {
        state.storeCurrentLoading = true;
        state.error = null;
      })
      .addCase(fetchStoreSubscriptions.rejected, (state, action) => {
        state.storeCurrentLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchStoreSubscriptionPlans.fulfilled, (state, action) => {
        state.storeSubscriptionPlans = action.payload || [];
        state.storePlansLoading = false;
        state.error = null;
      })
      .addCase(fetchStoreSubscriptionPlans.pending, (state) => {
        state.storePlansLoading = true;
        state.error = null;
      })
      .addCase(fetchStoreSubscriptionPlans.rejected, (state, action) => {
        state.storePlansLoading = false;
        state.error = action.payload;
      })

      .addCase(createStoreSubscription.fulfilled, (state) => {
        state.storeCheckoutLoading = false;
        state.error = null;
      })
      .addCase(createStoreSubscription.pending, (state) => {
        state.storeCheckoutLoading = true;
        state.error = null;
      })
      .addCase(createStoreSubscription.rejected, (state, action) => {
        state.storeCheckoutLoading = false;
        state.error = action.payload;
      })

      .addCase(changeStoreSubscription.fulfilled, (state) => {
        state.storeCheckoutLoading = false;
        state.error = null;
      })
      .addCase(changeStoreSubscription.pending, (state) => {
        state.storeCheckoutLoading = true;
        state.error = null;
      })
      .addCase(changeStoreSubscription.rejected, (state, action) => {
        state.storeCheckoutLoading = false;
        state.error = action.payload;
      })

      .addCase(verifyStoreSubscription.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(verifyStoreSubscription.rejected, (state) => {
        // Verify is a UX hint; avoid clobbering list UI with verify errors
        state.error = null;
      })

      .addCase(cancelStoreSubscription.fulfilled, (state) => {
        state.storeCancelLoading = false;
        state.error = null;
      })
      .addCase(cancelStoreSubscription.pending, (state) => {
        state.storeCancelLoading = true;
        state.error = null;
      })
      .addCase(cancelStoreSubscription.rejected, (state, action) => {
        state.storeCancelLoading = false;
        state.error = action.payload;
      })

      .addCase(assignStoreSubscription.fulfilled, (state, action) => {
        if (state.storeSubscriptionStatus && action.payload) {
          const list = state.storeSubscriptionStatus.history || [];
          state.storeSubscriptionStatus.activeSubscription = action.payload;
          state.storeSubscriptionStatus.history = [action.payload, ...list];
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(assignStoreSubscription.pending, (state) => setLoading(state, true))
      .addCase(assignStoreSubscription.rejected, (state, action) => setError(state, action))

      .addCase(updateStoreSubscriptionOverrides.fulfilled, (state, action) => {
        if (
          state.storeSubscriptionStatus?.activeSubscription &&
          action.payload?.id === state.storeSubscriptionStatus.activeSubscription.id
        ) {
          state.storeSubscriptionStatus.activeSubscription = action.payload;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updateStoreSubscriptionOverrides.pending, (state) => setLoading(state, true))
      .addCase(updateStoreSubscriptionOverrides.rejected, (state, action) => setError(state, action))

      .addCase(getStoreSubscriptionPlan.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(getStoreSubscriptionPlan.pending, (state) => setLoading(state, true))
      .addCase(getStoreSubscriptionPlan.rejected, (state, action) => setError(state, action));
  },
});

export const { clearPlanDetail, clearStoreSubscriptionStatus, clearError } =
  subscriptionSlice.actions;
export default subscriptionSlice.reducer;
